/**
 * The transcript.
 *
 * Layout follows T3's message lane: the user's turn is a filled bubble on the
 * right, the assistant's is plain text on the left with no bubble, and the work
 * log sits indented under it. Assistant text is deliberately not boxed — a long
 * answer in a bubble is much harder to read, and it is the thing on screen most
 * of the time.
 */

import { memo, useMemo } from "react";
import type { MessagePart, TranscriptEntry } from "../lib/models";
import { renderMarkdown } from "../app/markdown";
import { toBlocks, WorkRow } from "./work-log";
import { IconAlert, IconTerminal } from "./icons";

export function Transcript({
  entries,
  historyPending,
  onRespond
}: {
  entries: readonly TranscriptEntry[];
  historyPending: boolean;
  onRespond: (inputId: string, answers: Record<string, string>) => void;
}) {
  return (
    <div className="mx-auto flex w-full max-w-[var(--chat-content-max-width)] flex-col gap-6 px-4 py-6">
      {historyPending ? (
        <p className="text-center text-xs text-muted-foreground">Loading earlier messages…</p>
      ) : null}
      {entries.map((entry) => (
        <Entry key={entry.id} entry={entry} onRespond={onRespond} />
      ))}
    </div>
  );
}

const Entry = memo(function Entry({
  entry,
  onRespond
}: {
  entry: TranscriptEntry;
  onRespond: (inputId: string, answers: Record<string, string>) => void;
}) {
  const blocks = useMemo(() => toBlocks(entry.parts, entry.id), [entry.parts, entry.id]);

  if (entry.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-message px-3.5 py-2 text-sm whitespace-pre-wrap text-message-foreground">
          {blocks.map((block) =>
            block.kind === "text" ? (
              <span key={block.key}>{block.part.kind === "text" ? block.part.text : ""}</span>
            ) : block.kind === "other" && block.part.kind === "image" ? (
              // The engine sends a path, not bytes: the file lives on the host
              // and there is no route that streams it to the browser yet, so the
              // name is shown rather than a broken image.
              <span key={block.key} className="mt-1 block text-xs text-muted-foreground">
                {block.part.name}
              </span>
            ) : null
          )}
        </div>
      </div>
    );
  }

  if (entry.role === "system") {
    return (
      <p className="text-center text-xs text-muted-foreground">
        {blocks
          .map((block) =>
            block.kind === "text" && block.part.kind === "text" ? block.part.text : ""
          )
          .join(" ")
          .trim()}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {blocks.map((block) => {
        if (block.kind === "work") {
          return <WorkRow key={block.key} items={block.items} errors={block.errors} />;
        }
        if (block.kind === "text" && block.part.kind === "text") {
          return <Markdown key={block.key} source={block.part.text} />;
        }
        if (block.kind === "other") {
          return <OtherPart key={block.key} part={block.part} onRespond={onRespond} />;
        }
        return null;
      })}
      {entry.status === "aborted" ? (
        <p className="text-xs text-muted-foreground italic">Stopped.</p>
      ) : null}
    </div>
  );
});

/**
 * Assistant text.
 *
 * `renderMarkdown` returns sanitized HTML (it escapes first and builds from a
 * fixed tag set), so `dangerouslySetInnerHTML` here is not the usual hazard — the
 * alternative would be re-parsing the markdown into React nodes on every streamed
 * token, which is exactly the cost this rewrite is removing. Memoized on the
 * source so a token that changed a later block does not re-render this one.
 */
const Markdown = memo(function Markdown({ source }: { source: string }) {
  const html = useMemo(() => renderMarkdown(source), [source]);
  return (
    <div
      className="prose-chat text-sm leading-relaxed text-foreground"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

/** Errors, prompts for input, and anything else that is not prose. */
function OtherPart({
  part,
  onRespond
}: {
  part: MessagePart;
  onRespond: (inputId: string, answers: Record<string, string>) => void;
}) {
  if (part.kind === "error") {
    return (
      <div className="flex items-start gap-2 rounded-lg bg-error-surface px-3 py-2 text-sm text-error-foreground">
        <IconAlert className="mt-0.5 size-4 shrink-0" />
        <span className="min-w-0 whitespace-pre-wrap">{part.message}</span>
      </div>
    );
  }
  if (part.kind === "input") {
    return <InputPrompt part={part} onRespond={onRespond} />;
  }
  if (part.kind === "fork") {
    return (
      <p className="text-xs text-muted-foreground">
        Continued from another conversation.
      </p>
    );
  }
  if (part.kind === "image") {
    // A path, not bytes — see the note in the user bubble above.
    return <span className="text-xs text-muted-foreground">{part.name}</span>;
  }
  return null;
}

/** An agent question. T3 surfaces this as a card; so does this. */
function InputPrompt({
  part,
  onRespond
}: {
  part: Extract<MessagePart, { kind: "input" }>;
  onRespond: (inputId: string, answers: Record<string, string>) => void;
}) {
  const questions = part.questions ?? [];
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <IconTerminal className="size-3.5" />
        Waiting for you
      </p>
      {questions.map((question) => (
        <div key={question.id} className="flex flex-col gap-1.5">
          <p className="text-sm text-foreground">{question.question}</p>
          <div className="flex flex-wrap gap-1.5">
            {(question.options ?? []).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => onRespond(part.requestId, { [question.id]: option })}
                className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm hover:bg-accent"
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
